/**
 * SETTINGS HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getUserSettings() - GET /settings?user_id=X&organisation_id=Y - Fetch resolved user settings
 * • updateWalletSort() - PUT /settings/wallet-sort - Save the wallet list order
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useUserSettings(userId, organisationId, enabled?) - Query: Get resolved user settings
 * • useUpdateWalletSort() - Mutation: Save the wallet list order
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • 5-minute stale time for settings data
 * • The backend already resolves org vs user level — no FE resolution needed
 * • instant_card_topups: true when enabled at org level OR custom user override
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import fetchAPI from '@/lib/fetch-api';

// ==================== INTERFACES ====================

/**
 * Response structure for user-level settings (already resolved by backend).
 * GET /settings?user_id=X&organisation_id=Y returns the effective value
 * taking both org-level and user-level overrides into account.
 */
export interface UserSettings {
  organisation_id: number;
  user_id: number;
  auto_withdrawals_to_cards: boolean;
  instant_card_topups: boolean;
  wallet_sort?: WalletSort;
}

export type WalletSort = 'default' | 'balance_desc' | 'balance_asc';

// ==================== API FUNCTIONS ====================

/**
 * Fetches resolved settings for a user within an organisation context.
 * The backend computes the effective value (org default + user override).
 * GET /settings?user_id=X&organisation_id=Y
 *
 * @param userId - The user ID to fetch settings for
 * @param organisationId - The organisation ID context
 * @throws {Error} If the API request fails
 * @returns {Promise<UserSettings>} The resolved user settings
 */
const getUserSettings = async (userId: number, organisationId: number): Promise<UserSettings> => {
  const response = await fetchAPI<UserSettings>(
    `/settings?user_id=${userId}&organisation_id=${organisationId}`,
    { method: 'GET' }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch user settings');
  }

  return response.data!;
};

/**
 * Saves the wallet list order for the logged-in user in their active organisation.
 * PUT /settings/wallet-sort
 */
const updateWalletSort = async (sort: WalletSort): Promise<void> => {
  const response = await fetchAPI('/settings/wallet-sort', {
    method: 'PUT',
    body: JSON.stringify({ sort }),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to save wallet sort');
  }
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch resolved user settings including instant_card_topups flag.
 *
 * @param userId - The user ID to fetch settings for
 * @param organisationId - The organisation ID context
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns {UseQueryResult} Query result for user settings
 *
 * @example
 * const { data: settings } = useUserSettings(userId, orgId);
 * const isInstantTopUp = settings?.instant_card_topups ?? false;
 */
export function useUserSettings(
  userId: number,
  organisationId: number,
  enabled: boolean = true
): UseQueryResult<UserSettings, Error> {
  return useQuery({
    queryKey: ['user-settings', organisationId, userId],
    queryFn: () => getUserSettings(userId, organisationId),
    enabled: enabled && !!userId && !!organisationId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to save the wallet list order. GET /wallets applies it, so wallets are refetched.
 */
export function useUpdateWalletSort(): UseMutationResult<void, Error, WalletSort> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWalletSort,
    onMutate: (sort) => {
      queryClient.setQueriesData<UserSettings>({ queryKey: ['user-settings'] }, (settings) =>
        settings ? { ...settings, wallet_sort: sort } : settings
      );
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['user-settings'] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallets'] }),
  });
}
